import React, { useState, useEffect } from "react";
import { Modal } from "@/components/Modal";
import { Input } from "@/components/Input";
import { Textarea } from "@/components/Textarea";
import { PrioritySelect } from "@/components/PrioritySelect";
import { Select } from "@/components/Select";
import { Switch } from "@/components/Switch";
import { developmentApi, tasksApi, authApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";

interface SuggestedTask {
    title: string;
    description: string;
    priority: number;
    assignee_id: number | null;
    selected: boolean;
}

interface SuggestTasksModalProps {
    isOpen: boolean;
    onClose: () => void;
    developmentId: number;
    onSuccess: () => void;
}

export const SuggestTasksModal: React.FC<SuggestTasksModalProps> = ({
    isOpen,
    onClose,
    developmentId,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [suggestions, setSuggestions] = useState<SuggestedTask[]>([]);
    const [hasSuggested, setHasSuggested] = useState(false);
    const [users, setUsers] = useState<any[]>([]);

    useEffect(() => {
        if (isOpen) {
            authApi
                .users()
                .then((res: any) => {
                    if (res.result && Array.isArray(res.data)) {
                        setUsers(res.data);
                    }
                })
                .catch(() => {});
        }
    }, [isOpen]);

    const handleSuggest = async (): Promise<void> => {
        setLoading(true);
        try {
            const res = await developmentApi.suggestTasks(developmentId);
            if (res.result && Array.isArray(res.data)) {
                setSuggestions(
                    res.data.map((task: any) => ({
                        ...task,
                        assignee_id: null,
                        selected: true,
                    }))
                );
                setHasSuggested(true);
            } else {
                showError(t("errors.default"));
            }
        } catch (error) {
            console.error("[SuggestTasks] exception", error);
            showError(t("errors.default"));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (): Promise<void> => {
        const selectedTasks = suggestions.filter((s) => s.selected);
        if (selectedTasks.length === 0) {
            showError(
                t("developments.detail.suggest_tasks_error_none_selected")
            );
            return;
        }

        setSaving(true);
        try {
            for (const task of selectedTasks) {
                await tasksApi.create({
                    title: task.title,
                    description: task.description,
                    priority: task.priority,
                    development_id: developmentId,
                    assignee_id: task.assignee_id || null,
                    status_id: 1,
                });
            }
            showSuccess(t("developments.detail.suggest_tasks_success"));
            onSuccess();
            onClose();
        } catch (error) {
            showError(t("errors.default"));
        } finally {
            setSaving(false);
        }
    };

    const updateTask = (
        index: number,
        field: keyof SuggestedTask,
        value: any
    ): void => {
        const newSuggestions = [...suggestions];
        newSuggestions[index] = { ...newSuggestions[index], [field]: value };
        setSuggestions(newSuggestions);
    };

    const allSelected =
        suggestions.length > 0 && suggestions.every((s) => s.selected);

    const toggleAll = (val: boolean): void => {
        setSuggestions(suggestions.map((s) => ({ ...s, selected: val })));
    };

    const userOptions = [
        { value: "", label: t("developments.detail.suggest_tasks_unassigned") },
        ...users.map((u: any) => ({ value: String(u.id), label: u.name })),
    ];

    if (!hasSuggested) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={t("developments.detail.suggest_tasks_modal_title")}
                submitLabel={t(
                    "developments.detail.suggest_tasks_modal_submit"
                )}
                onSubmit={handleSuggest}
                isSubmitting={loading}
                maxWidth="md"
            >
                <div className="text-center py-2">
                    <Icon
                        value="sparkles"
                        size={48}
                        className="text-primary mx-auto mb-4"
                    />
                    <p className="text-slate-600 dark:text-zinc-400 mb-2">
                        {t("developments.detail.suggest_tasks_modal_desc1")}
                    </p>
                    <p className="text-sm text-slate-500">
                        {t("developments.detail.suggest_tasks_modal_desc2")}
                    </p>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("developments.detail.suggest_tasks_modal_title_suggested")}
            submitLabel={t(
                "developments.detail.suggest_tasks_modal_submit_selected"
            )}
            onSubmit={handleSave}
            isSubmitting={saving}
            maxWidth="3xl"
        >
            {/* Toggle all */}
            <div className="flex justify-center mb-4">
                <button
                    type="button"
                    onClick={() => toggleAll(!allSelected)}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-slate-200 dark:border-zinc-700 bg-slate-50 dark:bg-zinc-800/60 hover:bg-slate-100 dark:hover:bg-zinc-700/60 transition-colors cursor-pointer w-full text-xs font-medium text-slate-600 dark:text-zinc-300"
                >
                    {allSelected
                        ? t("developments.detail.suggest_tasks_deselect_all", {
                              count: suggestions.length,
                          })
                        : t("developments.detail.suggest_tasks_select_all", {
                              count: suggestions.length,
                          })}
                </button>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto px-1">
                {suggestions.map((task, idx) => (
                    <div
                        key={idx}
                        className={`p-4 rounded-xl border transition-all ${
                            task.selected
                                ? "border-primary/40 bg-primary/5 dark:bg-primary/10"
                                : "border-slate-200 bg-slate-50 dark:bg-zinc-800/50 dark:border-zinc-700 opacity-60"
                        }`}
                    >
                        <div className="space-y-3">
                            {/* Switch + Title inline */}
                            <div className="flex items-center gap-3">
                                <Switch
                                    checked={task.selected}
                                    onChange={(val: boolean) =>
                                        updateTask(idx, "selected", val)
                                    }
                                    size="sm"
                                />
                                <div className="flex-1">
                                    <Input
                                        value={task.title}
                                        onChange={(
                                            e: React.ChangeEvent<HTMLInputElement>
                                        ) =>
                                            updateTask(
                                                idx,
                                                "title",
                                                e.target.value
                                            )
                                        }
                                        placeholder={t(
                                            "developments.detail.suggest_tasks_placeholder_title"
                                        )}
                                    />
                                </div>
                            </div>

                            {/* Description: label + fullscreen button on same row via Textarea's label prop */}
                            <Textarea
                                label={t(
                                    "developments.detail.suggest_tasks_label_desc"
                                )}
                                value={task.description}
                                onChange={(
                                    e: React.ChangeEvent<HTMLTextAreaElement>
                                ) =>
                                    updateTask(
                                        idx,
                                        "description",
                                        e.target.value
                                    )
                                }
                                placeholder={t(
                                    "developments.detail.suggest_tasks_placeholder_desc"
                                )}
                                rows={4}
                                fullscreenTitle={task.title}
                                autoGrow={true}
                            />

                            {/* Priority */}
                            <div>
                                <label className="block text-xs font-medium text-slate-500 dark:text-zinc-400 mb-1">
                                    {t(
                                        "developments.detail.suggest_tasks_label_priority"
                                    )}
                                </label>
                                <PrioritySelect
                                    value={task.priority}
                                    onChange={(val: number) =>
                                        updateTask(idx, "priority", val)
                                    }
                                    full
                                />
                            </div>

                            {/* Assignee */}
                            <div className="w-full">
                                <label className="block text-xs font-medium text-slate-500 dark:text-zinc-400 mb-1">
                                    {t(
                                        "developments.detail.suggest_tasks_label_assignee"
                                    )}
                                </label>
                                <Select
                                    value={
                                        task.assignee_id
                                            ? String(task.assignee_id)
                                            : ""
                                    }
                                    onChange={(val: string) =>
                                        updateTask(
                                            idx,
                                            "assignee_id",
                                            val ? parseInt(val) : null
                                        )
                                    }
                                    options={userOptions}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
};
